import React, { useEffect, useState } from "react";
import { Box, Typography, useMediaQuery } from "@mui/material";
import { useDispatch } from "react-redux";
import { useHistory } from "react-router-dom";
import { getPaymentLink } from "apis/v3/authApi";
import { checkoutUsesTopLevelNavigation } from "constants/checkoutNavigationConstants";
import PageTitle from "utils/PageTitle";
import GradientFramedLayout from "components/v3/layouts/GradientFramedLayout";
import LoadingAnimation from "components/v3/sharedComponents/LoadingAnimation";
import { openGLSnackbar } from "store/reducers/snackbar/openGLSnackbarSlice";
import { COLORS } from "components/v3/Theme/colors";

const LOADING_VIEWPORT_MAX_PX = 500;
const IFRAME_MIN_HEIGHT_PX = 850;

/** Extra min-height when checkout stacks fields (avoids inner iframe scroll). */
const IFRAME_MIN_HEIGHT_NARROW_EXTRA_PX = 200;
const IFRAME_NARROW_MAX_WIDTH_CSS = "(max-width: 526px)";
const EXPAND_MAX_PX = 1600;

/** White frame around iframe (checkout is cross-origin; we can’t style inside). */
const IFRAME_ROUNDED_CORNER_RADIUS_PX = 10;
const IFRAME_ROUNDED_CORNER_PADDING_PX = 10;
const IFRAME_CHROME_VERTICAL_EXTRA_PX = IFRAME_ROUNDED_CORNER_PADDING_PX * 2;

const MembershipPaymentPage: React.FC = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const isNarrowPaymentViewport = useMediaQuery(IFRAME_NARROW_MAX_WIDTH_CSS, {
    defaultMatches: false
  });
  const iframeMinHeightLoaded =
    IFRAME_MIN_HEIGHT_PX +
    (isNarrowPaymentViewport ? IFRAME_MIN_HEIGHT_NARROW_EXTRA_PX : 0);
  const outerMinHeightLoading =
    LOADING_VIEWPORT_MAX_PX + IFRAME_CHROME_VERTICAL_EXTRA_PX;
  const outerMinHeightLoaded =
    iframeMinHeightLoaded + IFRAME_CHROME_VERTICAL_EXTRA_PX;
  const outerMaxHeightLoaded = EXPAND_MAX_PX + IFRAME_CHROME_VERTICAL_EXTRA_PX;
  const [ecommerceLink, setEcommerceLink] = useState<string | null>(null);
  const [iframeLoaded, setIframeLoaded] = useState(false);

  useEffect(() => {
    setIframeLoaded(false);
  }, [ecommerceLink]);

  useEffect(() => {
    const handleLoadFailure = () => {
      dispatch(
        openGLSnackbar({
          variant: "error",
          header: "",
          subText: "Something went wrong, please try again."
        })
      );
      history.goBack();
    };

    getPaymentLink()
      .then((response) => {
        const link =
          response && "data" in response && response.data?.data?.ecommerceLink;
        if (link) {
          if (checkoutUsesTopLevelNavigation) {
            globalThis.location.replace(link);
            return;
          }
          setEcommerceLink(link);
        } else {
          handleLoadFailure();
        }
      })
      .catch(() => {
        handleLoadFailure();
      });
  }, [dispatch, history]);

  PageTitle("Renew membership");

  if (!ecommerceLink) {
    return (
      <GradientFramedLayout showHeader={true}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            maxHeight: LOADING_VIEWPORT_MAX_PX,
            minHeight: 360,
            mx: "auto"
          }}
        >
          <LoadingAnimation />
        </Box>
      </GradientFramedLayout>
    );
  }

  return (
    <GradientFramedLayout showHeader={true}>
      <Box
        sx={{
          alignItems: "stretch",
          alignSelf: "stretch",
          boxSizing: "border-box",
          display: "flex",
          flexDirection: "column",
          gap: 3,
          marginX: "auto",
          maxWidth: { md: 740, xs: "100%" },
          minWidth: 0,
          paddingBottom: "40px",
          paddingTop: { md: 3, xs: 2 },
          paddingX: { md: 3, xs: 2 },
          width: "100%"
        }}
      >
        <Typography
          variant="h4_primary"
          component="h1"
          sx={{ fontWeight: 700, textAlign: "left", width: "100%" }}
        >
          Continue your membership
        </Typography>
        <Box
          sx={{
            position: "relative",
            boxSizing: "border-box",
            maxHeight: iframeLoaded
              ? outerMaxHeightLoaded
              : LOADING_VIEWPORT_MAX_PX + IFRAME_CHROME_VERTICAL_EXTRA_PX,
            maxWidth: "100%",
            minHeight: iframeLoaded
              ? outerMinHeightLoaded
              : outerMinHeightLoading,
            minWidth: 0,
            mx: "auto",
            overflow: iframeLoaded ? "visible" : "hidden",
            py: { xs: 2, md: 3 },
            px: 0,
            transition: (theme) =>
              theme.transitions.create(["max-height", "min-height"], {
                duration: 450,
                easing: theme.transitions.easing.easeInOut
              }),
            width: "100%"
          }}
        >
          <Box
            sx={{
              backgroundColor: COLORS.WHITE,
              borderRadius: `${IFRAME_ROUNDED_CORNER_RADIUS_PX}px`,
              boxSizing: "border-box",
              maxWidth: "100%",
              minWidth: 0,
              overflow: "hidden",
              p: `${IFRAME_ROUNDED_CORNER_PADDING_PX}px`,
              width: "100%"
            }}
          >
            <iframe
              key={ecommerceLink}
              title="Membership payment"
              src={ecommerceLink}
              onLoad={() => setIframeLoaded(true)}
              style={{
                border: "none",
                display: "block",
                marginLeft: "auto",
                marginRight: "auto",
                maxWidth: "100%",
                minHeight: iframeLoaded
                  ? iframeMinHeightLoaded
                  : LOADING_VIEWPORT_MAX_PX,
                minWidth: 0,
                opacity: iframeLoaded ? 1 : 0,
                transition:
                  "min-height 450ms cubic-bezier(0.4, 0, 0.2, 1), opacity 0.25s ease-in",
                width: "100%"
              }}
            />
          </Box>
          {!iframeLoaded && (
            <Box
              sx={{
                position: "absolute",
                inset: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                bgcolor: "transparent",
                p: { xs: 2, md: 3 }
              }}
            >
              <LoadingAnimation />
            </Box>
          )}
        </Box>
        <Typography
          variant="body_dark_gray_2"
          sx={{
            color: COLORS.DARK_GRAY_2,
            lineHeight: 1.65,
            mb: 2,
            textAlign: "center",
            fontSize: "13px"
          }}
        >
          Having issues reactivating your membership? Reach out to our team and
          we’ll get you sorted 1-on-1.
          <br />
          Renewing using a gift membership is not currently possible online—but
          our team would be happy to help.
        </Typography>
      </Box>
    </GradientFramedLayout>
  );
};

export default MembershipPaymentPage;
